/**
 * Per-page text extraction via pdf.js, used to power in-document search.
 *
 * This reads the text that is actually encoded in the PDF (the original or
 * reopened bytes). Pending add-text edits are not searchable until the file is
 * saved and reopened — they live in the edit log, not yet in the page content.
 */
import { loadViewerDocument } from './pdf-render.js';
import type { PageText, TextItemGeom } from './search.js';

/**
 * Extract one searchable string per page. A pdf.js text item flagged `hasEOL`
 * ends a line, so we insert a space to avoid gluing the last word of a line to
 * the first of the next (which would break substring search across line breaks).
 */
export async function extractDocumentText(bytes: Uint8Array): Promise<PageText[]> {
  const doc = await loadViewerDocument(bytes);
  try {
    const pages: PageText[] = [];
    for (let n = 1; n <= doc.numPages; n++) {
      const page = await doc.getPage(n);
      const content = await page.getTextContent();
      let text = '';
      const items: TextItemGeom[] = [];
      for (const item of content.items) {
        if (!('str' in item)) continue; // skip TextMarkedContent markers
        const start = text.length;
        text += item.str;
        // transform = [a, b, c, d, e, f]; (e, f) is the run's baseline position in
        // PDF user space. Box: x = e, width = item.width; baseline = f, height =
        // item.height — dropped 20% below the baseline so it covers descenders.
        const t = item.transform;
        const x = t[4] ?? 0;
        const baseline = t[5] ?? 0;
        const h = item.height || Math.abs(t[3] ?? 0);
        if (item.str.length > 0 && item.width > 0 && h > 0) {
          items.push({
            start,
            length: item.str.length,
            rect: { x, y: baseline - h * 0.2, width: item.width, height: h },
          });
        }
        if (item.hasEOL) text += ' ';
      }
      pages.push({ pageIndex: n - 1, text, items });
      page.cleanup();
    }
    return pages;
  } finally {
    void doc.destroy();
  }
}
